// tab/src/context/TeamContext.tsx
// OnRounds Phase 2 - Team Context Provider

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import * as microsoftTeams from '@microsoft/teams-js';
import { api } from '../services/api';
import { useUser } from './UserContext';

interface Team {
  id: string;
  name: string;
  organisationId: string;
}

interface TeamContextType {
  currentTeam: Team | null;
  teams: Team[];
  isLoading: boolean;
  error: string | null;
  setCurrentTeam: (team: Team | null) => void;
  refreshTeams: () => Promise<void>;
}

const TeamContext = createContext<TeamContextType | undefined>(undefined);

export const TeamProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isLoading: userLoading } = useUser();
  const [currentTeam, setCurrentTeam] = useState<Team | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshTeams = useCallback(async () => {
    alert(`TeamContext: refreshTeams called. user.id: ${user?.id || 'NULL'}, userLoading: ${userLoading}`);
    if (!user?.id) {
      console.log('No user ID yet, skipping team load');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      const result = await api.getTeams(user.teamsUserId);
      setTeams(result.teams as Team[]);
      
      // Auto-select first team if none selected
      if (!currentTeam && result.teams.length > 0) {
        setCurrentTeam(result.teams[0] as Team);
      }
    } catch (err) {
      console.error('Failed to load teams:', err);
      setError('Failed to load teams');
    } finally {
      setIsLoading(false);
    }
  }, [currentTeam, user?.id]);

  useEffect(() => {
    alert(`TeamContext useEffect: userLoading=${userLoading}, user.id=${user?.id || 'NULL'}`);
    if (userLoading || !user?.id) {
      return;
    }
    refreshTeams();
  }, [user?.id, userLoading, refreshTeams]);

  return (
    <TeamContext.Provider
      value={{
        currentTeam,
        teams,
        isLoading,
        error,
        setCurrentTeam,
        refreshTeams,
      }}
    >
      {children}
    </TeamContext.Provider>
  );
};

export const useTeam = (): TeamContextType => {
  const context = useContext(TeamContext);
  if (context === undefined) {
    throw new Error('useTeam must be used within a TeamProvider');
  }
  return context;
};
